import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { ShiftSummaryClient, ShiftSummary, ShiftSummaryAttachment, ShiftSummaryPartCTestStatus } from '../../../services/nswag/nswagclient';
import {
  formatPartCTestStatus as formatPartCTestStatusLabel,
  getFluidTypeLabel,
  isImageAttachment as isImageFileAttachment,
  showsPartCDetails,
  supportsCurrentFluidSystem as supportsCurrentFluidSystemForProfile,
  supportsFoamMetrics as supportsFoamMetricsForProfile
} from '../shift-summary-create/shift-summary-create.helpers';
import { ChemicalDosageChartComponent } from '../chemical-dosage-chart/chemical-dosage-chart';

@Component({
  selector: 'app-shift-summary-view',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    PanelModule,
    ProgressSpinnerModule,
    TableModule,
    ChemicalDosageChartComponent
  ],
  templateUrl: './shift-summary-view.component.html',
  styleUrls: ['./shift-summary-view.component.css']
})
export class ShiftSummaryViewComponent implements OnInit {
  summary$!: Observable<ShiftSummary | null>;
  loading = signal(true);
  attachmentPreviewUrls = signal<Record<string, string>>({});
  jobId = '';
  shiftSummaryId = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private shiftSummaryClient: ShiftSummaryClient
  ) {}

  ngOnInit(): void {
    this.jobId = this.route.snapshot.paramMap.get('id') ?? '';
    this.shiftSummaryId = this.route.snapshot.paramMap.get('shiftSummaryId') ?? '';

    this.summary$ = this.shiftSummaryClient.getById(this.shiftSummaryId).pipe(
      tap(summary => {
        this.loading.set(false);
        void this.loadAttachmentPreviewUrls(summary?.attachments);
      }),
      catchError(err => {
        console.error('Error loading shift summary', err);
        this.loading.set(false);
        this.attachmentPreviewUrls.set({});
        return of(null);
      })
    );
  }

  back(): void {
    this.router.navigate(['/vendorjob', this.jobId, 'shiftsummaries']);
  }

  openDashboard(url: string | undefined): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  formatNA(value: any): string {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    return String(value);
  }

  formatYesNo(value: boolean | null | undefined): string {
    if (value === true) {
      return 'Yes';
    }

    if (value === false) {
      return 'No';
    }

    return 'N/A';
  }

  formatNumber(value: number | undefined | null, decimals: number = 2): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    return value.toFixed(decimals);
  }

  formatPercentage(value: number | undefined | null): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    return `${value.toFixed(2)}%`;
  }

  supportsFoamMetrics(summaryProfile: ShiftSummary['summaryProfile']): boolean {
    return supportsFoamMetricsForProfile(summaryProfile);
  }

  supportsCurrentFluidSystem(summaryProfile: ShiftSummary['summaryProfile']): boolean {
    return supportsCurrentFluidSystemForProfile(summaryProfile);
  }

  fluidTypeLabel(summaryProfile: ShiftSummary['summaryProfile']): string {
    return getFluidTypeLabel(summaryProfile);
  }

  formatPartCTestStatus(value: ShiftSummaryPartCTestStatus | undefined | null): string {
    return formatPartCTestStatusLabel(value);
  }

  showPartCDetails(value: ShiftSummaryPartCTestStatus | undefined | null): boolean {
    return showsPartCDetails(value);
  }

  isImageAttachment(attachment: ShiftSummaryAttachment): boolean {
    return isImageFileAttachment(attachment.contentType, attachment.fileName);
  }

  getAttachmentPreviewUrl(attachment: ShiftSummaryAttachment): string | null {
    return attachment.id ? (this.attachmentPreviewUrls()[attachment.id] ?? null) : null;
  }

  async downloadAttachment(attachmentId: string | undefined): Promise<void> {
    if (!attachmentId) return;
    try {
      const url = await firstValueFrom(
        this.shiftSummaryClient.getAttachmentDownloadUrl(this.shiftSummaryId, attachmentId)
      );
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading attachment', err);
    }
  }

  private async loadAttachmentPreviewUrls(attachments: ShiftSummaryAttachment[] | undefined): Promise<void> {
    const imageAttachments = (attachments ?? [])
      .filter(attachment => !!attachment.id && this.isImageAttachment(attachment));

    if (!imageAttachments.length) {
      this.attachmentPreviewUrls.set({});
      return;
    }

    const previewEntries = await Promise.all(imageAttachments.map(async attachment => {
      try {
        const url = await firstValueFrom(
          this.shiftSummaryClient.getAttachmentDownloadUrl(this.shiftSummaryId, attachment.id!)
        );
        return [attachment.id!, url] as const;
      } catch (error) {
        console.error('Error loading shift summary view attachment preview', error);
        return [attachment.id!, null] as const;
      }
    }));

    this.attachmentPreviewUrls.set(
      Object.fromEntries(previewEntries.filter((entry): entry is readonly [string, string] => !!entry[1]))
    );
  }
}
